
export class EntitySchemaPropertyItem implements EntitySchemaProperty {
	static idSequence: number = 1;

	public id: string;
	private behavior: EntitySchemaPropertyItemBehavior;
	private _type: EntitySchemaPropertyType;
	public config: { min?: number; max?: number; pattern?: string, multi?: boolean; allowNewTags?: boolean; };

	public get type(): EntitySchemaPropertyType {
		return this._type;
	}
	public set type(value: EntitySchemaPropertyType) {
		for (let behavior of EntitySchemaPropertyBuilder.definedBehaviors) {
			if (behavior.type === value) {
				this.behavior = behavior;
				break;
			}
		}

		this._type = value;
	}

	constructor(
		public name: string = '',
		public label: string = '',
		type: EntitySchemaPropertyType = 'text',
		public required: boolean = false,
		config = {},
		public options: string[] = [],
		public taxonomy: string = null) {
		this.type = type;
		if (config != null) {
			this.config = {
				min: config['min'],
				max: config['max'],
				pattern: config['pattern'],
				multi: config['multi'],
				allowNewTags: config['allowNewTags']
			};
		}

		this.id = (EntitySchemaPropertyItem.idSequence++).toString();
	}

	validate(): React.ReactNode[] {
		return this.behavior.validate(this);
	}

	toJsonSchemaProperty(): any {
		const jsonSchemaProperty: JsonSchema = this.behavior.asJsonSchema(this);

		return {
			[this.name]: jsonSchemaProperty
		};
	}
}

abstract class EntitySchemaPropertyItemBehavior {
	abstract type: EntitySchemaPropertyType;

	abstract asJsonSchema(self: EntitySchemaPropertyItem): JsonSchema;
	convert(name: string, isRequired: boolean, schema: JsonSchema): EntitySchemaPropertyItem {
		return new EntitySchemaPropertyItem(name, schema.title, this.type, isRequired);
	}
	abstract match(schema: JsonSchema): boolean;
	validate(self: EntitySchemaPropertyItem): React.ReactNode[] {
		const errors: React.ReactNode[] = [];
		if (self.name === '') {
			errors.push('Name required');
		} else {
			if (self.name.search(' ') > -1) {
				errors.push(`Name '${self.name}' can not have empty spaces`);
			}
			if (self.name[0].toUpperCase() === self.name[0]) {
				errors.push(`Name '${self.name}'can not start with uppercase`);
			}
		}
		if (self.label === '') {
			errors.push('Label required');
		}

		return errors;
	}
}

class BooleanBehavior extends EntitySchemaPropertyItemBehavior {
	type: EntitySchemaPropertyType = 'boolean';
	asJsonSchema(self: EntitySchemaPropertyItem): JsonSchema {
		return {
			title: self.label,
			type: 'boolean'
		};
	}
	match(schema: JsonSchema): boolean {
		return schema.type === 'boolean';
	}
}

class DateBehavior extends EntitySchemaPropertyItemBehavior {
	type: EntitySchemaPropertyType = 'date';
	asJsonSchema(self: EntitySchemaPropertyItem): JsonSchema {
		return {
			title: self.label,
			type: 'string',
			format: 'date-time'
		};
	}
	match(schema: JsonSchema): boolean {
		return schema.type === 'string' && schema.format === 'date-time';
	}
}

class EmailBehavior extends EntitySchemaPropertyItemBehavior {
	type: EntitySchemaPropertyType = 'email';
	asJsonSchema(self: EntitySchemaPropertyItem): JsonSchema {
		return {
			title: self.label,
			type: 'string',
			format: 'email'
		};
	}
	match(schema: JsonSchema): boolean {
		return schema.type === 'string' && schema.format === 'email';
	}
}

class OptionsBehavior extends EntitySchemaPropertyItemBehavior {
	type: EntitySchemaPropertyType = 'options';
	asJsonSchema(self: EntitySchemaPropertyItem): JsonSchema {
		return {
			title: self.label,
			type: 'string',
			enum: self.options
		};
	}
	convert(name: string, isRequired: boolean, schema: JsonSchema): EntitySchemaPropertyItem {
		const options = schema.enum;
		return new EntitySchemaPropertyItem(name, schema.title, this.type, isRequired, {}, options);
	}
	match(schema: JsonSchema): boolean {
		return schema.type === 'string' && schema.enum != null;
	}
	validate(self: EntitySchemaPropertyItem): React.ReactNode[] {
		const errors = super.validate(self);
		const options = self.options;
		if (options.length === 0) {
			errors.push('It must be at least one option');
		}

		return errors;
	}
}

class TaxonomyBehavior extends EntitySchemaPropertyItemBehavior {
	type: EntitySchemaPropertyType = 'taxonomy';
	asJsonSchema(self: EntitySchemaPropertyItem): JsonSchema {
		const jsonSchema: JsonSchema = {
			title: self.label,
			_config: { allowNewTags: self.config.allowNewTags }
		};

		if (self.config.multi) {
			jsonSchema.items = {
				$ref: self.taxonomy
			};
		} else {
			jsonSchema.$ref = self.taxonomy;
		}

		return jsonSchema;
	}
	convert(name: string, isRequired: boolean, schema: JsonSchema): EntitySchemaPropertyItem {
		const config = {
			allowNewTags: schema._config.allowNewTags,
			multi: false
		};
		let taxonomy: string;

		if (schema.$ref != null) {
			taxonomy = schema.$ref;
			config.multi = false;
		} else {
			taxonomy = schema.items.$ref;
			config.multi = true;
		}

		return new EntitySchemaPropertyItem(name, schema.title, this.type, isRequired, config, [], taxonomy);
	}
	match(schema: JsonSchema): boolean {
		return schema.$ref != null || (schema.items != null && schema.items.$ref != null);
	}
	validate(self: EntitySchemaPropertyItem): React.ReactNode[] {
		const errors = super.validate(self);
		if (self.taxonomy == null || self.taxonomy == '') {
			errors.push('Taxonomy is required');
		}

		return errors;
	}
}

class TextBehavior extends EntitySchemaPropertyItemBehavior {
	type: EntitySchemaPropertyType = 'text';
	asJsonSchema(self: EntitySchemaPropertyItem): JsonSchema {
		const jsonSchemaProperty = {
			title: self.label,
			type: 'string' as JsonSchemaType
		};

		const config = self.config;
		if (config) {
			if (config.min != null) {
				jsonSchemaProperty['minLength'] = config.min;
			}
			if (config.max != null) {
				jsonSchemaProperty['maxLength'] = config.max;
			}
			if (config.pattern != null) {
				jsonSchemaProperty['pattern'] = config.min;
			}
		}

		return jsonSchemaProperty;
	}
	convert(name: string, isRequired: boolean, schema: JsonSchema): EntitySchemaPropertyItem {
		const constraints = { min: schema.minLength, max: schema.maxLength, pattern: schema.pattern };
		return new EntitySchemaPropertyItem(name, schema.title, this.type, isRequired, constraints);
	}
	match(schema: JsonSchema): boolean {
		return schema.type === 'string' && schema.format == null && schema.enum == null;
	}
	validate(self: EntitySchemaPropertyItem): React.ReactNode[] {
		const errors = super.validate(self);
		const minText = self.config.min && self.config.min.toString().trim();
		const min = parseInt(minText);
		if (minText) {
			if (Number.isNaN(min)) {
				errors.push('"Min" must be an integer');
			} else {
				self.config.min = min; // normalizing
			}
		}

		const maxText = self.config.max && self.config.max.toString().trim();
		const max = parseInt(maxText);
		if (maxText) {
			if (Number.isNaN(max)) {
				errors.push('"Max" must be an integer');
			} else {
				self.config.max = max; // normalizing
			}
		}

		if (!Number.isNaN(min) && !Number.isNaN(max) && min > max) {
			errors.push('"Min" must be less than "Max"');
		}

		return errors;
	}
}

class UrlBehavior extends EntitySchemaPropertyItemBehavior {
	type: EntitySchemaPropertyType = 'url';
	asJsonSchema(self: EntitySchemaPropertyItem): JsonSchema {
		return {
			title: self.label,
			type: 'string',
			format: 'uri'
		};
	}
	match(schema: JsonSchema): boolean {
		return schema.type === 'string' && schema.format === 'uri';
	}
}

export class EntitySchemaPropertyBuilder {
	static definedBehaviors: EntitySchemaPropertyItemBehavior[] = [
		new BooleanBehavior(), new DateBehavior(), new EmailBehavior(), new OptionsBehavior(), new TaxonomyBehavior(), new TextBehavior(), new UrlBehavior()
	];

	static getAdditionalProperties(jsonSchema: JsonSchema): EntitySchemaPropertyItem[] {
		if (jsonSchema.allOf == null) {
			return new Array<EntitySchemaPropertyItem>();
		}

		const { properties, required } = jsonSchema.allOf[0];

		return Object.keys(properties)
			.map((propertyName) => {
				const jsc = properties[propertyName] as JsonSchema;
				const isRequired = required.some((p) => p === propertyName);
				return EntitySchemaPropertyBuilder.fromJsonSchema(propertyName, isRequired, jsc);
			});
	}

	static fromJsonSchema(name: string, isRequired: boolean, obj: JsonSchema): EntitySchemaPropertyItem {
		for (let behavior of EntitySchemaPropertyBuilder.definedBehaviors) {
			if (behavior.match(obj)) {
				return behavior.convert(name, isRequired, obj);
			}
		}

		throw Error('Not implemented yet!');
	}
}



// WEBPACK FOOTER //
// ./src/app/general/entity-schemas/form/entity-schema-form.data.ts